import styled from 'styled-components';
import { NoDataComponent } from '../../../adminComponents/NoDataComponent/NoDataComponent';

/**
 * Centres the line in the space that is actually free, which is not the same as
 * the box it is in.
 *
 * The conversation clears the floating title bar with padding at the top but
 * has none at the bottom, where the composer floats over it - so a plain centre
 * lands half a composer too low. Measured at 23px off before this. The variable
 * is published by the thread; on any surface without a composer - the inbox
 * column - it resolves to nothing and this is a plain centre again.
 */
const Frame = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: var(--chat-composer-height, 0px);

  /* NoDataComponent's line ships a 32px bottom margin, sized for a page where
     something follows it. Inside a box being centred it is dead space in the
     middle of the thing itself, and it lifts the line off centre by half. The
     heading's top margin is the same story from the other end. */
  p {
    margin-bottom: 0;
  }

  /* Both of the heading's margins: the top one lifts the pair off centre, and
     the bottom one is a page-sized gap between two lines that belong to each
     other. What is left between them is the line's own 8px. */
  h3 {
    margin-top: 0;
    margin-bottom: 0;
  }
`;

/**
 * What a chat surface shows when there is nothing in it.
 *
 * One component for every one of those moments - no conversation picked, a
 * thread with no messages yet, a filtered inbox that matched nothing - because
 * they were four hand-written lines of grey text before, and anything added to
 * three of them is how the fourth ends up looking broken.
 *
 * Text only. It carried an illustration for a while; what is left here is the
 * centring, which is the part none of those four surfaces gets right on its
 * own.
 */
export const ChatEmptyState = ({
  title,
  text,
}: {
  /** Optional heading above the line, for the surfaces that invite an answer. */
  title?: string;
  text: string;
}) => (
  <Frame>
    <NoDataComponent
      title={title}
      description={text}
      /* Nothing of its own: the frame above is what does the centring, and a
         min-height here would fight the padding that keeps it clear of the
         composer. The component's own default is viewport-relative, which is
         right on a page and wrong in a box already the height of one. */
      height="auto"
      heightSM="auto"
    />
  </Frame>
);
